'use client';
/**
 * @file     Navbar.tsx
 * @readme   ./README.md
 * @purpose  Sticky glass site header: the Relta wordmark with the theme switch beside it, a bare rail of sentence-case groups with one sliding indicator, Sign in or the account menu, the Find homes button that opens the Home Finder, and the mobile menu sheet. CSS modules keep Emotion off the critical path.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { chromeLayoutTokens, semanticRoleColors, zLayers } from '@/design-system/tokens';
import { navbarCategories, routes } from '@/core/constants';
import {
  NAV_RAIL_CATEGORY_KEYS,
  isHomeFinderActionHidden,
  resolveActiveCategoryKey,
} from '@/core/config/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useNavigationCategories } from '@/hooks/useNavigationCategories';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { PropertyNotificationBadge } from '@/components/layout/PropertyNotificationBadge';
import { NavRailIcon } from './NavRailIcon';
import { ThemeToggle } from './ThemeToggle';
import { useSignInLink } from './useSignInLink';
import type { NavbarProps } from './Navbar.types';
import styles from './Navbar.module.css';

const NavbarDrawer = dynamic(() => import('./NavbarDrawer'), { ssr: false });
const NavbarMenu = dynamic(() => import('./NavbarMenu'), { ssr: false });
const NavbarAuthButton = dynamic(
  () => import('./NavbarAuthButton').then((m) => ({ default: m.NavbarAuthButton })),
  { ssr: false },
);

type HomeFinderModalComponent = typeof import('./HomeFinderModal')['HomeFinderModal'];

let homeFinderModalPromise: Promise<HomeFinderModalComponent> | null = null;

const loadHomeFinderModal = () => {
  homeFinderModalPromise ??= import('./HomeFinderModal').then((module) => module.HomeFinderModal);
  return homeFinderModalPromise;
};

const MOBILE_SHEET_ID = 'site-menu-sheet';

const SearchGlyph = ({ size = 20, strokeWidth = 1.8 }: { size?: number; strokeWidth?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" aria-hidden="true" focusable="false">
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l5 5" />
  </svg>
);

const AccountGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true" focusable="false">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M19 20c0-2.8-3.1-5-7-5s-7 2.2-7 5" />
  </svg>
);

const ChevronDownGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const MenuGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

type IndicatorBox = { left: number; width: number } | null;

const Navbar = ({
  categories: categoriesProp = navbarCategories,
  brand,
}: NavbarProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { trackCtaClick } = useAnalytics();
  const signIn = useSignInLink();
  const categories = useNavigationCategories(categoriesProp);
  const railCategories = NAV_RAIL_CATEGORY_KEYS
    .map((key) => categories.find((category) => category.key === key))
    .filter((category): category is NonNullable<typeof category> => Boolean(category));
  // The rental search is its own page (/rent), so the path alone places it under Rent.
  const activeKey = resolveActiveCategoryKey(categories, pathname);
  const showFindHomes = !isHomeFinderActionHidden(pathname);

  const homeLabel = t('nav.actions.brandHome', { defaultValue: 'Relta home' });
  const brandLabel = t('common.brand', { defaultValue: 'Relta' });
  const signInLabel = t('nav.actions.signIn', { defaultValue: 'Sign in' });
  const accountLabel = t('nav.actions.account', { defaultValue: 'Account' });
  const findHomesLabel = t('nav.actions.findHomes', { defaultValue: 'Find homes' });
  const openFinderLabel = t('nav.actions.openHomeFinder', { defaultValue: 'Find homes: open the home finder' });

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuCategoryKey, setMenuCategoryKey] = useState<string | null>(null);
  const [categoryMenuLoaded, setCategoryMenuLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuLoaded, setMobileMenuLoaded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [homeFinderOpen, setHomeFinderOpen] = useState(false);
  const [HomeFinderModal, setHomeFinderModal] = useState<HomeFinderModalComponent | null>(null);
  const [indicator, setIndicator] = useState<IndicatorBox>(null);

  const headerRef = useRef<HTMLElement>(null);
  const railRef = useRef<HTMLElement>(null);
  const railItemRefs = useRef(new Map<string, HTMLElement>());
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const openHomeFinder = useCallback(() => {
    setHomeFinderOpen(true);
    void loadHomeFinderModal().then((component) => setHomeFinderModal(() => component));
  }, []);

  // Deep link from ads/social: `?finder=ai` auto-opens the Home Finder into the
  // AI search (optional `&fq=<prose>` prefills it). One-shot per page load.
  const [finderInitial, setFinderInitial] = useState<{ step: 'ai'; query: string } | undefined>(undefined);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('finder') !== 'ai') return;
    setFinderInitial({ step: 'ai', query: sp.get('fq') ?? '' });
    openHomeFinder();
    sp.delete('finder');
    sp.delete('fq');
    const qs = sp.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  }, [openHomeFinder]);

  useEffect(() => {
    setMenuAnchor(null);
    setMobileMenuOpen(false);
  }, [pathname]);

  // Publish the header's bottom edge to :root so siblings (e.g. CompareTray) and
  // sticky children (--chrome-inset) sit right below it. The header is the top
  // of the chrome stack, so only its own height can move that edge.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      const bottom = el.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--nav-height', `${Math.max(0, bottom)}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const scrollRoot = document.querySelector('[data-app-scroll-root="true"]') as HTMLElement | null;
    const scrollSource = scrollRoot ?? window;
    const threshold = theme.layout.sectionPaddingVertical.xs;
    const handleScroll = () => {
      const nextOffset = scrollRoot?.scrollTop ?? window.scrollY ?? 0;
      setIsScrolled(nextOffset > threshold);
    };
    handleScroll();
    scrollSource.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollSource.removeEventListener('scroll', handleScroll);
  }, [theme.layout.sectionPaddingVertical.xs]);

  // One indicator slides between rail items instead of each item owning a bar.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const measure = () => {
      const item = activeKey ? railItemRefs.current.get(activeKey) : undefined;
      if (!item || item.offsetWidth === 0) {
        setIndicator(null);
        return;
      }
      setIndicator({ left: item.offsetLeft, width: item.offsetWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(rail);
    return () => ro.disconnect();
  }, [activeKey]);

  const openCategoryMenu = (categoryKey: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (menuAnchor && menuCategoryKey === categoryKey) {
      setMenuAnchor(null);
      return;
    }
    setCategoryMenuLoaded(true);
    setMenuCategoryKey(categoryKey);
    setMenuAnchor(event.currentTarget);
    if (categoryKey === 'rent') trackCtaClick('nav_rent_menu', pathname ?? '', undefined, { cta_id: 'nav_rent_menu' });
  };

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  const navigateTo = (path: string) => {
    const ctaId = categories.flatMap((category) => category.items).find((item) => item.path === path)?.ctaId;
    if (ctaId) trackCtaClick(ctaId, pathname ?? '', path, { cta_id: ctaId });
    setMenuAnchor(null);
    setMobileMenuOpen(false);
    router.push(path);
  };

  const cssVars = {
    '--nav-z': zLayers.appBar,
    '--nav-border': theme.semanticColors.borderSubtle,
    '--nav-text-primary': theme.semanticColors.textPrimary,
    '--nav-text-secondary': theme.semanticColors.textSecondary,
    '--nav-text-muted': theme.semanticColors.textMuted,
    '--nav-primary': theme.semanticColors.interactivePrimary,
    '--nav-primary-hover': semanticRoleColors.primaryHover,
    '--nav-on-primary': theme.semanticColors.textOnPrimary,
    '--nav-tooltip-text': theme.semanticColors.backgroundPrimary,
    '--nav-hover-surface': theme.colors.hoverSurface,
    '--nav-focus-ring': theme.semanticColors.interactiveFocusRing,
    '--nav-radius-sm': `${theme.layout.radius.sm}px`,
    '--nav-radius-md': `${theme.layout.radius.md}px`,
    '--nav-radius-pill': `${theme.layout.radius.pill}px`,
    // Chrome spans the viewport (shared with the footer), capped for wide screens.
    '--nav-max-width': `${chromeLayoutTokens.maxWidth}px`,
    '--nav-gutter': chromeLayoutTokens.gutter.fluid,
    '--nav-heading-font': theme.typography.fontFamilyHeading,
  } as React.CSSProperties;

  const menuCategory = categories.find((category) => category.key === menuCategoryKey);

  return (
    <header ref={headerRef} className={`${styles.appBar} r-glass-chrome`} data-scrolled={isScrolled || undefined} style={cssVars}>
      <div className={styles.toolbar}>
        <div className={styles.brandGroup}>
          <Link
            href={routes.home}
            className={styles.wordmarkLink}
            onClick={() => setMobileMenuOpen(false)}
            aria-label={homeLabel}
          >
            {brandLabel}
          </Link>
          <ThemeToggle />
        </div>

        <nav
          ref={railRef}
          className={styles.rail}
          aria-label={t('nav.actions.primary', { defaultValue: 'Primary' })}
        >
          <ul className={styles.railList}>
            {railCategories.map((category) => {
              const isActive = category.key === activeKey;
              const className = `${styles.railItem} ${isActive ? styles.railItemActive : ''}`;
              const content = (
                <>
                  <span className={styles.railIcon}>
                    <NavRailIcon categoryKey={category.key} />
                  </span>
                  {t(category.labelKey)}
                </>
              );
              const setRef = (el: HTMLElement | null) => {
                if (el) railItemRefs.current.set(category.key, el);
                else railItemRefs.current.delete(category.key);
              };
              return (
                <li key={category.key} className={styles.railLi}>
                  {category.directLink ? (
                    <Link
                      ref={setRef}
                      href={category.directLink}
                      className={className}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => setMenuAnchor(null)}
                    >
                      {content}
                    </Link>
                  ) : (
                    <button
                      ref={setRef}
                      type="button"
                      className={className}
                      aria-haspopup="menu"
                      aria-expanded={Boolean(menuAnchor) && menuCategoryKey === category.key}
                      onClick={(event) => openCategoryMenu(category.key, event)}
                    >
                      {content}
                      <ChevronDownGlyph />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <span
            aria-hidden="true"
            className={styles.indicator}
            style={
              indicator
                ? { opacity: 1, width: indicator.width, transform: `translateX(${indicator.left}px)` }
                : undefined
            }
          />
        </nav>

        <div className={styles.desktopRight}>
          {isAuthenticated ? (
            <NavbarAuthButton />
          ) : (
            <PropertyNotificationBadge>
              <Link href={signIn.href} onClick={signIn.onClick} onPointerDown={signIn.onPointerDown} onFocus={signIn.onFocus} className={styles.signInLink}>
                {signInLabel}
              </Link>
            </PropertyNotificationBadge>
          )}
          {showFindHomes ? (
            <button
              type="button"
              className={styles.findHomes}
              onClick={openHomeFinder}
              aria-label={openFinderLabel}
            >
              <SearchGlyph size={15} strokeWidth={2.2} />
              {findHomesLabel}
            </button>
          ) : null}
        </div>

        <div className={styles.mobileControls}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={openHomeFinder}
            aria-label={openFinderLabel}
          >
            <SearchGlyph />
          </button>
          <PropertyNotificationBadge>
            <Link
              href={isAuthenticated ? '/dashboard' : signIn.href}
              onClick={isAuthenticated ? undefined : signIn.onClick}
              onPointerDown={isAuthenticated ? undefined : signIn.onPointerDown}
              onFocus={isAuthenticated ? undefined : signIn.onFocus}
              className={`${styles.iconBtn} ${isAuthenticated ? styles.iconBtnSignedIn : ''}`}
              aria-label={isAuthenticated ? accountLabel : signInLabel}
            >
              <span className={styles.accountDisc}>
                <AccountGlyph />
              </span>
            </Link>
          </PropertyNotificationBadge>
          <button
            ref={menuButtonRef}
            type="button"
            className={styles.iconBtn}
            aria-label={t('common.navigation.menu', { defaultValue: 'Open menu' })}
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuOpen ? MOBILE_SHEET_ID : undefined}
            aria-haspopup="dialog"
            onClick={() => {
              setMobileMenuLoaded(true);
              setMobileMenuOpen((current) => !current);
            }}
          >
            <MenuGlyph />
          </button>
        </div>
      </div>

      {categoryMenuLoaded ? (
        <NavbarMenu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor && menuCategory)}
          onClose={() => setMenuAnchor(null)}
          category={menuCategory}
          pathname={pathname}
          onNavigate={navigateTo}
        />
      ) : null}

      {mobileMenuLoaded ? (
        <NavbarDrawer
          id={MOBILE_SHEET_ID}
          open={mobileMenuOpen}
          onClose={closeMobileMenu}
          onDismiss={() => setMobileMenuOpen(false)}
          categories={categories}
          activeKey={activeKey}
          pathname={pathname}
          brand={brand}
          isAuthenticated={isAuthenticated}
          showFindHomes={showFindHomes}
          onFindHomes={() => {
            setMobileMenuOpen(false);
            openHomeFinder();
          }}
        />
      ) : null}

      {HomeFinderModal ? (
        <HomeFinderModal
          open={homeFinderOpen}
          onClose={() => { setHomeFinderOpen(false); setFinderInitial(undefined); }}
          initialStep={finderInitial?.step}
          initialAiQuery={finderInitial?.query}
        />
      ) : null}
    </header>
  );
};

export default Navbar;
